import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Label,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { TermPreviewModal } from "@/components/company/TermPreviewModal";
import { hedgesCompanyApi, companyApi, type Hedge } from "@/services/api";
import {
  Shield,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
  Wallet,
  DollarSign,
  ArrowRight,
  Info,
  RefreshCw,
  FileText,
  TrendingUp,
  TrendingDown,
  Clock,
} from "lucide-react";

function formatUsdc(value: string | number | undefined): string {
  if (!value) return "$0.00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatBrl(value: string | number | undefined): string {
  if (!value) return "R$ 0,00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatRate(value: string | number | undefined): string {
  if (!value) return "R$ 0,0000";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return `R$ ${num.toFixed(4)}`;
}

function formatPercent(value: number): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
}

// Tipo para resposta do refresh quote
interface RefreshQuoteResponse {
  hedgeId: string;
  original: {
    spotRate: string;
    finalRate: string;
    lockedAmountBrl: string;
    protectionFeeBrl: string;
    operationFeeBrl: string;
    capitalCostBrl: string;
  };
  updated: {
    spotRate: string;
    finalRate: string;
    lockedAmountBrl: string;
    protectionFeeBrl: string;
    operationFeeBrl: string;
    capitalCostBrl: string;
  };
  guaranteeAmountUsdc: string;
  validUntil: string;
  variationPercent: number;
}

// Tipo para preview do termo
interface TermPreviewData {
  html: string;
  data: {
    hedgeCode: string;
    hedgeAmountUsdc: string;
    durationDays: number;
    startDate: string;
    endDate: string;
    lockedAmountBrl: string;
    protectionFeeBrl: string;
    companyName: string;
    companyCnpj: string;
  };
}

// Indicador de etapa
type Step = "quote" | "term" | "deposit";

export function GarantiaHedgePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [hedge, setHedge] = useState<Hedge | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<{ usdc: number; brld: number }>({ usdc: 0, brld: 0 });
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Estado das 3 etapas
  const [currentStep, setCurrentStep] = useState<Step>("quote");

  // Etapa 1: Cotação
  const [refreshing, setRefreshing] = useState(false);
  const [quoteData, setQuoteData] = useState<RefreshQuoteResponse | null>(null);
  const [countdown, setCountdown] = useState<number>(0);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  // Etapa 2: Termo
  const [termPreview, setTermPreview] = useState<TermPreviewData | null>(null);
  const [loadingTerm, setLoadingTerm] = useState(false);
  const [showTermModal, setShowTermModal] = useState(false);
  const [termAccepted, setTermAccepted] = useState(false);

  // Etapa 3: Depósito
  const [depositLoading, setDepositLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [hedgeData, dashboardData] = await Promise.all([
        hedgesCompanyApi.getHedge(id),
        companyApi.dashboard.get(),
      ]);
      setHedge(hedgeData);

      // Extrair saldos do dashboard
      const usdcBalance = dashboardData.balance?.usdcBalance || 0;
      const brldBalance = dashboardData.balance?.availableBalance || 0;
      setBalance({ usdc: usdcBalance / 100, brld: brldBalance / 100 });
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
      setError("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh da cotação se hedge foi criado recentemente (fluxo contínuo)
  const autoRefreshDone = useRef(false);
  useEffect(() => {
    if (!hedge || autoRefreshDone.current || quoteData || refreshing) return;

    const createdAt = new Date(hedge.createdAt).getTime();
    const now = Date.now();
    const elapsedSeconds = (now - createdAt) / 1000;

    // Se o hedge foi criado há menos de 60 segundos, fazer refresh automático
    if (elapsedSeconds < 60) {
      autoRefreshDone.current = true;
      handleRefreshQuote();
    }
  }, [hedge]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup do countdown ao desmontar
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  // Iniciar countdown quando tiver uma cotação válida
  const startCountdown = useCallback((validUntil: string) => {
    // Limpar countdown anterior
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const expiry = new Date(validUntil).getTime();
      const remaining = Math.max(0, Math.floor((expiry - now) / 1000));
      setCountdown(remaining);

      if (remaining <= 0 && countdownRef.current) {
        clearInterval(countdownRef.current);
        // Cotação expirou - precisa renovar
        setQuoteData(null);
        setTermAccepted(false);
        setCurrentStep("quote");
      }
    };

    updateCountdown();
    countdownRef.current = setInterval(updateCountdown, 1000);
  }, []);

  // Etapa 1: Renovar cotação
  const handleRefreshQuote = async () => {
    if (!id) return;

    try {
      setRefreshing(true);
      setError(null);

      const response = await hedgesCompanyApi.refreshQuote(id);
      setQuoteData(response);
      startCountdown(response.validUntil);

      // Recarregar hedge para ter valores atualizados
      const updatedHedge = await hedgesCompanyApi.getHedge(id);
      setHedge(updatedHedge);

      // Avançar para etapa do termo (só precisa renovar uma vez)
      setCurrentStep("term");
    } catch (err: unknown) {
      console.error("Erro ao renovar cotação:", err);
      const errorMessage = err instanceof Error ? err.message : "Erro ao renovar cotação";
      setError(errorMessage);
    } finally {
      setRefreshing(false);
    }
  };

  // Etapa 2: Carregar preview do termo
  const handleLoadTermPreview = async () => {
    if (!id) return;

    try {
      setLoadingTerm(true);
      setError(null);

      const response = await hedgesCompanyApi.getTermPreviewForGuarantee(id);
      setTermPreview(response);
      setShowTermModal(true);
    } catch (err: unknown) {
      console.error("Erro ao carregar termo:", err);
      const errorMessage = err instanceof Error ? err.message : "Erro ao carregar termo";
      setError(errorMessage);
    } finally {
      setLoadingTerm(false);
    }
  };

  // Etapa 2: Aceitar termo
  const handleAcceptTerm = () => {
    setTermAccepted(true);
    setShowTermModal(false);
    setCurrentStep("deposit");
  };

  // Etapa 3: Depositar garantia
  const handleDeposit = async () => {
    if (!hedge || !id) return;

    // Validar que cotação ainda é válida
    if (countdown <= 0) {
      setError("Cotação expirada. Renove a cotação antes de continuar.");
      setCurrentStep("quote");
      setTermAccepted(false);
      return;
    }

    try {
      setDepositLoading(true);
      setError(null);

      // Chamar endpoint de depósito de garantia com aceite do termo
      await hedgesCompanyApi.depositGuarantee(id, { acceptedTerms: true });

      setShowSuccessModal(true);
    } catch (err: unknown) {
      console.error("Erro ao depositar garantia:", err);
      const errorMessage = err instanceof Error ? err.message : "Erro ao depositar garantia";
      setError(errorMessage);
    } finally {
      setDepositLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hedge) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle className="w-12 h-12 text-muted-foreground" />
        <p className="text-muted-foreground">Hedge não encontrado</p>
        <Button onClick={() => navigate("/protecao-cambial")}>
          Voltar para Lista
        </Button>
      </div>
    );
  }

  if (hedge.status !== "PENDING_GUARANTEE") {
    // Redirecionar se não está aguardando garantia
    navigate(`/protecao-cambial/${id}`);
    return null;
  }

  const guaranteeRequired = parseFloat(hedge.guaranteeAmountUsdc || "0");
  const hasEnoughBalance = balance.usdc >= guaranteeRequired;
  const missingAmount = guaranteeRequired - balance.usdc;

  // Verificar se cotação está válida
  const quoteIsValid = countdown > 0;

  // Formatar data de criação
  const createdAtFormatted = hedge.createdAt
    ? new Date(hedge.createdAt).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Wallet className="h-5 w-5 sm:h-6 sm:w-6" />}
        title="Depositar Garantia"
        description={`Hedge ${hedge.hedgeCode}`}
        actions={
          <Button
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => navigate(`/protecao-cambial/${id}`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        }
      />

      {/* Error */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Progresso das etapas */}
      <div className="flex items-center justify-center gap-1 sm:gap-2 py-4">
        <div className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg ${currentStep === "quote" ? "bg-primary text-white" : quoteIsValid ? "bg-green-100 text-green-700" : "bg-neutral-100 text-neutral-500"}`}>
          <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium border-2 border-current">
            {quoteIsValid && currentStep !== "quote" ? <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" /> : "1"}
          </span>
          <span className="font-medium text-sm sm:text-base">Cotação</span>
        </div>
        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-neutral-300 flex-shrink-0" />
        <div className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg ${currentStep === "term" ? "bg-primary text-white" : termAccepted ? "bg-green-100 text-green-700" : "bg-neutral-100 text-neutral-500"}`}>
          <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium border-2 border-current">
            {termAccepted ? <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" /> : "2"}
          </span>
          <span className="font-medium text-sm sm:text-base">Termo</span>
        </div>
        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-neutral-300 flex-shrink-0" />
        <div className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg ${currentStep === "deposit" ? "bg-primary text-white" : "bg-neutral-100 text-neutral-500"}`}>
          <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium border-2 border-current">3</span>
          <span className="font-medium text-sm sm:text-base">Depósito</span>
        </div>
      </div>

      {/* ETAPA 1: Atualizar Cotação */}
      <Card className={`border-2 ${currentStep === "quote" ? "border-primary/50" : quoteIsValid ? "border-green-200" : "border-neutral-200"}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className={`h-5 w-5 ${quoteIsValid ? "text-green-500" : "text-primary"}`} />
            Etapa 1: Atualizar Cotação
            {quoteIsValid && (
              <span className="ml-auto flex items-center gap-2 text-sm font-normal">
                <Clock className="w-4 h-4" />
                <span className={countdown <= 10 ? "text-orange-500 font-medium" : "text-green-600"}>
                  Válida por {countdown}s
                </span>
              </span>
            )}
          </CardTitle>
          <CardDescription>
            {quoteIsValid
              ? "Cotação atualizada com sucesso!"
              : `Esta operação foi criada em ${createdAtFormatted}. Atualize a cotação para valores atuais.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!quoteIsValid ? (
            <>
              <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium">Cotação desatualizada</p>
                  <p>A taxa de câmbio pode ter variado desde a criação do hedge. Atualize a cotação para prosseguir.</p>
                </div>
              </div>

              <Button
                onClick={handleRefreshQuote}
                disabled={refreshing}
                className="w-full"
                size="lg"
              >
                {refreshing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Atualizando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Atualizar Cotação
                  </>
                )}
              </Button>
            </>
          ) : quoteData && (
            <>
              {/* Comparativo de valores */}
              <div className="rounded-lg border border-neutral-200 overflow-hidden">
                <div className="bg-neutral-50 px-4 py-3 border-b border-neutral-200">
                  <h4 className="text-sm font-medium text-neutral-600">Comparativo de Valores</h4>
                </div>
                <div className="divide-y divide-neutral-100">
                  {/* Valor Final Garantido - DESTAQUE PRINCIPAL */}
                  <div className="flex items-center justify-between px-4 py-4 bg-green-50/50">
                    <span className="text-sm font-medium text-neutral-700">Valor final garantido em BRL</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-neutral-400 line-through">{formatBrl(quoteData.original.lockedAmountBrl)}</span>
                      <ArrowRight className="w-4 h-4 text-neutral-400" />
                      <span className="text-xl font-bold text-green-700">{formatBrl(quoteData.updated.lockedAmountBrl)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${quoteData.variationPercent >= 0 ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>
                        {quoteData.variationPercent >= 0 ? <TrendingUp className="w-3 h-3 inline mr-1" /> : <TrendingDown className="w-3 h-3 inline mr-1" />}
                        {formatPercent(quoteData.variationPercent)}
                      </span>
                    </div>
                  </div>

                  {/* Taxa Travada - secundária */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm text-muted-foreground">Taxa travada</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-neutral-400 line-through">{formatRate(quoteData.original.finalRate)}</span>
                      <ArrowRight className="w-4 h-4 text-neutral-400" />
                      <span className="text-sm font-medium text-neutral-900">{formatRate(quoteData.updated.finalRate)}</span>
                    </div>
                  </div>

                  {/* Fee de Proteção - consolidado */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm text-muted-foreground">Fee de proteção</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-neutral-400 line-through">{formatBrl(quoteData.original.protectionFeeBrl)}</span>
                      <ArrowRight className="w-4 h-4 text-neutral-400" />
                      <span className="font-medium text-neutral-900">{formatBrl(quoteData.updated.protectionFeeBrl)}</span>
                    </div>
                  </div>

                  {/* Garantia */}
                  <div className="flex items-center justify-between px-4 py-3 bg-neutral-50">
                    <span className="text-sm text-neutral-500">Garantia</span>
                    <span className="font-medium text-neutral-900">{formatUsdc(quoteData.guaranteeAmountUsdc)} <span className="text-xs text-neutral-400">(sem alteração)</span></span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Cotação atualizada!</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ETAPA 2: Aceitar Termo */}
      <Card className={`border-2 ${currentStep === "term" ? "border-primary/50" : termAccepted ? "border-green-200" : "border-neutral-200"} ${!quoteIsValid ? "opacity-50" : ""}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className={`h-5 w-5 ${termAccepted ? "text-green-500" : "text-primary"}`} />
            Etapa 2: Aceitar Termo de Proteção
            {termAccepted && (
              <span className="ml-auto flex items-center gap-2 text-green-600 text-sm font-normal">
                <CheckCircle className="w-4 h-4" />
                Termo aceito
              </span>
            )}
          </CardTitle>
          <CardDescription>
            {termAccepted
              ? "Você aceitou o termo de proteção cambial."
              : "Leia e aceite o termo de proteção cambial para prosseguir."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!termAccepted ? (
            <Button
              onClick={handleLoadTermPreview}
              disabled={!quoteIsValid || loadingTerm}
              className="w-full"
              size="lg"
              variant={currentStep === "term" ? "default" : "outline"}
            >
              {loadingTerm ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Carregando...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Ler e Aceitar Termo
                </>
              )}
            </Button>
          ) : (
            <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
              <CheckCircle className="h-5 w-5" />
              <span>Termo de proteção aceito</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ETAPA 3: Confirmar Depósito */}
      <Card className={`border-2 ${currentStep === "deposit" ? "border-primary/50" : "border-neutral-200"} ${!termAccepted ? "opacity-50" : ""}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            Etapa 3: Confirmar Depósito
          </CardTitle>
          <CardDescription>
            Confirme o depósito da garantia em USDC
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Saldo USDC */}
            <div className="p-4 rounded-lg bg-neutral-50 border border-neutral-200">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-neutral-500" />
                <Label className="text-neutral-500">Seu Saldo USDC</Label>
              </div>
              <p className="text-2xl font-bold text-neutral-900">{formatUsdc(balance.usdc)}</p>
            </div>

            {/* Garantia Necessária */}
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-primary" />
                <Label className="text-primary">Garantia Necessária</Label>
              </div>
              <p className="text-2xl font-bold text-primary">{formatUsdc(guaranteeRequired)}</p>
            </div>
          </div>

          {hasEnoughBalance ? (
            <>
              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Saldo suficiente para a garantia</span>
              </div>

              <p className="text-sm text-muted-foreground mt-4">
                Após o depósito da garantia, sua proteção cambial estará ativa e o valor final em reais ficará garantido até o vencimento.
              </p>

              <Button
                className="w-full"
                size="lg"
                onClick={handleDeposit}
                disabled={!termAccepted || !quoteIsValid || depositLoading}
              >
                {depositLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Wallet className="h-4 w-4 mr-2" />
                )}
                {depositLoading ? "Processando..." : "Confirmar Depósito de Garantia"}
              </Button>

              {!quoteIsValid && termAccepted && (
                <p className="text-xs text-center text-orange-500">
                  Cotação expirada. Volte à etapa 1 para renovar.
                </p>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-orange-600 bg-orange-50 p-3 rounded-lg">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">Faltam {formatUsdc(missingAmount)} USDC</span>
              </div>

              <Button
                className="w-full"
                size="lg"
                variant="outline"
                onClick={() => navigate("/converter")}
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Completar garantia em USDC
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Seu saldo BRLD: {formatBrl(balance.brld)}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Info sobre garantia */}
      <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium mb-1">Sobre a garantia em USDC</p>
          <p>Garantia temporária para cobrir variações cambiais extremas. É liberada automaticamente no vencimento da proteção.</p>
        </div>
      </div>

      {/* Modal de Termo */}
      {termPreview && (
        <TermPreviewModal
          open={showTermModal}
          onOpenChange={setShowTermModal}
          termHtml={termPreview.html}
          termData={termPreview.data}
          onAccept={handleAcceptTerm}
          loading={false}
        />
      )}

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 overflow-hidden">
            <CardContent className="pt-8 pb-6 space-y-6">
              {/* Header de sucesso */}
              <div className="text-center space-y-3">
                <div className="relative mx-auto w-20 h-20">
                  <div className="absolute inset-0 rounded-full bg-green-500/20 animate-pulse" />
                  <div className="relative w-20 h-20 rounded-full bg-green-500 flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-green-600">Garantia Depositada!</h2>
                <p className="text-muted-foreground">
                  Sua proteção cambial está aguardando aprovação do administrador. Você receberá um e-mail assim que for aprovada.
                </p>
              </div>

              {/* Resumo */}
              <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Garantia depositada</span>
                  <span className="font-semibold">{formatUsdc(hedge.guaranteeAmountUsdc)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Valor garantido</span>
                  <span className="font-semibold">{formatBrl(hedge.lockedAmountBrl)}</span>
                </div>
              </div>

              {/* Botão */}
              <Button className="w-full" onClick={() => navigate(`/protecao-cambial/${id}`)}>
                Ver Detalhes da Proteção
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
